import React, { useEffect, useMemo, useRef, useState } from 'react';
import { geoMercator, geoPath as d3GeoPath } from 'd3-geo';
import useGameStore from '../../game/gameStore';
import axios from 'axios';

const PARTY_COLORS = {
  PT: '#e00', PL: '#003da5', MDB: '#f08000', PP: '#003c8f',
  UNIÃO: '#2040a0', PSD: '#00c040', PSDB: '#0040c0', PDT: '#c03030',
  PSB: '#ff6000', REPUBLICANOS: '#0060a0', PSOL: '#f0d000', PODE: '#00a050',
  NOVO: '#f06000', PCdoB: '#c00020', AVANTE: '#f08020', CIDADANIA: '#a040c0',
  SOLIDARIEDADE: '#f06020', PV: '#00a000', REDE: '#00b0a0', PMN: '#c06000',
  DC: '#0040a0', PRTB: '#006040', PMB: '#a000c0', AGIR: '#3070c0',
};

const PLAYER_COLOR = '#4ade80';
const NEUTRAL_COLOR = '#2a3f5f';

function getPartyColor(partyId, playerParty) {
  if (partyId === playerParty) return PLAYER_COLOR;
  return PARTY_COLORS[partyId] || '#f87171';
}

function useContainerSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const roRef = useRef(null);
  const observedElRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el === observedElRef.current) return;

    if (roRef.current) roRef.current.disconnect();

    observedElRef.current = el;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setSize({ width, height });
      }
    });
    ro.observe(el);
    roRef.current = ro;
  });

  useEffect(() => {
    return () => {
      if (roRef.current) roRef.current.disconnect();
    };
  }, []);

  return size;
}

export default function TerritoryMap({ onTerritoryClick, selectedTerritory }) {
  const containerRef = useRef(null);
  const { width: cw, height: ch } = useContainerSize(containerRef);
  const [geoData, setGeoData] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [loading, setLoading] = useState(true);

  const territories = useGameStore(s => s.territories);
  const mapLevel = useGameStore(s => s.mapLevel);
  const cityData = useGameStore(s => s.cityData);
  const playerParty = useGameStore(s => s.playerParty);
  const turnPhase = useGameStore(s => s.turnPhase);
  const coalitionParties = useGameStore(s => s.coalitionParties);

  const alliedParties = useMemo(() => [playerParty, ...coalitionParties], [playerParty, coalitionParties]);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    const load = async () => {
      try {
        if (mapLevel === 'municipio' && cityData?.codigoIbge) {
          const res = await axios.get(`/geodata/territorios/${cityData.codigoIbge}`);
          if (alive && res.data?.success) setGeoData(res.data.data);
        } else if (mapLevel === 'estado' && cityData?.stateSigla) {
          const STATE_CODE_MAP = {
            AC:'12',AL:'27',AP:'16',AM:'13',BA:'29',CE:'23',DF:'53',ES:'32',GO:'52',
            MA:'21',MT:'51',MS:'50',MG:'31',PA:'15',PB:'25',PR:'41',PE:'26',PI:'22',
            RJ:'33',RN:'24',RS:'43',RO:'11',RR:'14',SC:'42',SP:'35',SE:'28',TO:'17',
          };
          const code = STATE_CODE_MAP[cityData.stateSigla.toUpperCase()];
          if (code) {
            const res = await axios.get(`/geodata/${code}`);
            if (alive) setGeoData(res.data);
          }
        } else if (mapLevel === 'pais') {
          const res = await fetch('https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson');
          if (alive) setGeoData(await res.json());
        }
      } catch (err) {
        console.error('TerritoryMap load error:', err);
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => { alive = false; };
  }, [mapLevel, cityData]);

  const mapW = cw > 100 ? cw : 600;
  const mapH = ch > 100 ? ch : 500;

  const { pathGen } = useMemo(() => {
    if (!geoData || mapW < 100) return { pathGen: null };
    const pad = 20;
    const proj = geoMercator().fitExtent([[pad, pad], [mapW - pad, mapH - pad]], geoData);
    return { pathGen: d3GeoPath().projection(proj) };
  }, [geoData, mapW, mapH]);

  const features = geoData?.features || [];

  const isVoronoi = geoData?.properties?.source === 'voronoi_bairros';

  const getTerritoryId = (feat, idx) => {
    if (mapLevel === 'municipio') {
      if (isVoronoi) return `bairro-${feat.properties?.id || (feat.properties?.zonaId ?? idx)}`;
      return `zona-${feat.properties?.zonaId ?? idx}`;
    }
    if (mapLevel === 'estado') return `mun-${feat.properties?.id || idx}`;
    return `estado-${feat.properties?.sigla || feat.properties?.UF_05 || idx}`;
  };

  const getTerritoryName = (feat) => {
    if (mapLevel === 'municipio') return feat.properties?.nome || '';
    return feat.properties?.name || feat.properties?.sigla || '';
  };

  const hoveredTerritoryData = hovered ? territories[hovered] : null;

  return (
    <div ref={containerRef} className="w-full h-full relative bg-[#060c18] overflow-hidden">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0a1020] z-30">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4ade80]" />
        </div>
      )}
      {/* Map level label */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
          {mapLevel === 'municipio' ? (isVoronoi ? 'Bairros do Município' : 'Zonas do Município') : mapLevel === 'estado' ? 'Municípios do Estado' : 'Estados do Brasil'}
        </span>
      </div>

      {/* Turn phase indicator */}
      <div className="absolute top-3 right-3 z-10">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
          turnPhase === 'allocate' ? 'bg-yellow-900/50 text-yellow-400 border border-yellow-700' :
          turnPhase === 'resolve' ? 'bg-blue-900/50 text-blue-400 border border-blue-700' :
          'bg-gray-900/50 text-gray-500 border border-gray-700'
        }`}>
          {turnPhase === 'event' ? 'Fase: Evento' : turnPhase === 'allocate' ? 'Fase: Alocar Recursos' : 'Fase: Resolução'}
        </span>
      </div>

      {/* Territory stats */}
      {Object.keys(territories).length > 0 && (
        <div className="absolute bottom-3 left-3 z-10 flex gap-2">
          <TerritoryCount label="Aliado" count={Object.values(territories).filter(t => alliedParties.includes(t.controlledBy)).length} color="#4ade80" />
          <TerritoryCount label="Rival" count={Object.values(territories).filter(t => t.controlledBy && !alliedParties.includes(t.controlledBy)).length} color="#f87171" />
          <TerritoryCount label="Neutro" count={Object.values(territories).filter(t => !t.controlledBy).length} color="#556688" />
        </div>
      )}

      {/* Hover tooltip */}
      {hovered && hoveredTerritoryData && (
        <div className="absolute bottom-3 right-3 z-10 bg-[#0a1424ee] border border-[#1a2848] rounded-lg p-3 min-w-[180px]">
          <p className="text-white font-bold text-sm">{hoveredTerritoryData.name}</p>
          <p className="text-gray-500 text-[10px]">Pop: {hoveredTerritoryData.population?.toLocaleString('pt-BR')}</p>
          <p className="text-gray-400 text-[10px] mt-1">
            Controle: <span style={{ color: hoveredTerritoryData.controlledBy ? getPartyColor(hoveredTerritoryData.controlledBy, playerParty) : '#556' }}>
              {hoveredTerritoryData.controlledBy || 'Neutro'}
            </span>
          </p>
          {Object.entries(hoveredTerritoryData.influence || {}).filter(([_, v]) => v > 1).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([p, v]) => (
            <div key={p} className="flex items-center gap-1 mt-0.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getPartyColor(p, playerParty) }} />
              <span className="text-[9px] text-gray-400">{p}: {Math.round(v)}%</span>
            </div>
          ))}
          {turnPhase === 'allocate' && (
            <p className="text-yellow-500 text-[9px] mt-1 font-bold">Clique para alocar recursos</p>
          )}
        </div>
      )}

      {/* SVG Map */}
      {pathGen && (
        <svg viewBox={`0 0 ${mapW} ${mapH}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%' }}>
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          {/* Fill layer */}
          {features.map((feat, i) => {
            const tId = getTerritoryId(feat, i);
            const t = territories[tId];
            const d = pathGen(feat);
            if (!d) return null;

            const isHovered = hovered === tId;
            const controlled = t?.controlledBy;

            let fill = NEUTRAL_COLOR;
            if (controlled) {
              fill = getPartyColor(controlled, playerParty);
            }

            return (
              <path
                key={tId}
                d={d}
                fill={fill}
                fillOpacity={isHovered ? 0.75 : 1}
                stroke="none"
                style={{ transition: 'fill 0.2s, fill-opacity 0.15s' }}
              />
            );
          })}
          {/* Border + interaction layer */}
          {features.map((feat, i) => {
            const tId = getTerritoryId(feat, i);
            const t = territories[tId];
            const d = pathGen(feat);
            if (!d) return null;

            const isHovered = hovered === tId;
            const isSelected = selectedTerritory === tId;
            const controlled = t?.controlledBy;
            const isAllied = controlled && alliedParties.includes(controlled);

            const strokeColor = isSelected ? '#d4a574'
              : isHovered ? '#ffffffaa'
              : isAllied ? '#4ade8066'
              : '#141f35';
            const sw = isSelected ? 2.5 : isHovered ? 2 : 1.2;

            return (
              <path
                key={`int-${tId}`}
                d={d}
                fill="transparent"
                stroke={strokeColor}
                strokeWidth={sw}
                cursor={turnPhase === 'allocate' ? 'pointer' : 'default'}
                style={{ transition: 'stroke 0.15s' }}
                filter={isSelected ? 'url(#glow)' : undefined}
                onMouseEnter={() => setHovered(tId)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onTerritoryClick?.(tId, getTerritoryName(feat))}
              />
            );
          })}
          {/* Labels layer */}
          {features.map((feat, i) => {
            const tId = getTerritoryId(feat, i);
            const t = territories[tId];
            const name = getTerritoryName(feat);
            if (!name || !pathGen) return null;
            const centroid = pathGen.centroid(feat);
            if (!centroid || isNaN(centroid[0])) return null;
            const controlled = t?.controlledBy;
            return (
              <g key={`label-${tId}`} pointerEvents="none">
                <text
                  x={centroid[0]} y={centroid[1]}
                  textAnchor="middle" dominantBaseline="central"
                  fill={controlled ? '#ffffffcc' : '#ffffff66'}
                  fontSize={mapLevel === 'pais' ? 8 : mapLevel === 'estado' ? 7 : 9}
                  fontWeight="600"
                  fontFamily="system-ui"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
                >
                  {name.length > 14 ? name.slice(0, 12) + '…' : name}
                </text>
                {controlled && (
                  <text
                    x={centroid[0]} y={centroid[1] + (mapLevel === 'pais' ? 10 : 12)}
                    textAnchor="middle" dominantBaseline="central"
                    fill="#ffffffaa"
                    fontSize={mapLevel === 'pais' ? 6 : 7}
                    fontFamily="system-ui"
                  >
                    {controlled}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function TerritoryCount({ label, count, color }) {
  return (
    <div className="flex items-center gap-1 bg-[#0a1424cc] border border-[#1a2848] rounded px-2 py-0.5">
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-[9px] text-gray-400">{count} {label}</span>
    </div>
  );
}
